/* eslint-disable @reatom/unit-naming-rule */
import { atom, type Atom, type Ctx } from '@reatom/framework'

import { AsyncStateN, Result } from '@blade/std-lib'
import { WrongCredentialsError } from '@root/features/auth'
import { CancelError, UnknownError } from '@root/shared'

import { WrongPasswordError } from '../secure-storage-error'

import type { Redacted } from '@blade/std-lib'

export interface EnterPasswordController {
  stateAtom: Atom<
    AsyncStateN.AsyncStateN<WrongPasswordError | UnknownError, unknown>
  >
  onPassword: (args: { password: Redacted.Redacted }) => void
  onCancel: () => void
}

export const enterPasswordControllerAtom = atom<
  EnterPasswordController | undefined
>(undefined)

export const requestLocalPasswordAction = async <E, A>(
  ctx: Ctx,
  onPassword: (
    ctx: Ctx,
    args: { password: Redacted.Redacted },
  ) => Promise<Result.Result<A, E>>,
): Promise<Result.Result<A, E | CancelError>> => {
  type Result_ = Result.Result<A, E | CancelError>
  let resolve: (value: Result_) => void

  const promise = new Promise<Result_>((res) => {
    resolve = res
  })

  const stateAtom = atom(
    AsyncStateN.notInitiated<WrongPasswordError | UnknownError, unknown>(),
  )

  const controller: EnterPasswordController = {
    stateAtom,
    onPassword: async ({ password }: { password: Redacted.Redacted }) => {
      stateAtom(ctx, AsyncStateN.loading())

      const result = await ctx.schedule(() => onPassword(ctx, { password }))
      const mappedResult = result.pipe(
        Result.mapError((it) =>
          it instanceof WrongCredentialsError ||
          it instanceof WrongPasswordError
            ? new WrongPasswordError()
            : UnknownError.fallbackToUnknown(it),
        ),
      )
      stateAtom(ctx, AsyncStateN.fromResult(mappedResult))

      resolve(mappedResult as Result_)

      if (Result.isSuccess(mappedResult)) {
        enterPasswordControllerAtom(ctx, undefined)
      }
    },
    onCancel: () => {
      enterPasswordControllerAtom(ctx, undefined)
      resolve(Result.fail(CancelError.make()))
    },
  }

  enterPasswordControllerAtom(ctx, controller)

  return promise
}
